import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { token } from "@atlaskit/tokens";

import { templateHeaderSectionRelayFragment$key } from "@/__generated__/templateHeaderSectionRelayFragment.graphql.ts";
import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages";

import { TemplateCtaButtons } from "../../template-details-page/template-cta-buttons";
import { TemplateAuthor } from "../components/template-author-relay";

const headerSectionStyles = css({
    "borderBlockEnd": "solid 1px",
    "borderBlockEndColor": token(
        "color.background.accent.gray.subtler.hovered"
    ),
    "display": "flex",
    "flexDirection": "column",
    "paddingBlockEnd": "24px",
    "@media(max-width: 768px)": {
        alignItems: "center"
    }
});

const titleStyles = css({
    "color": "#172B4D",
    "fontFamily": "Charlie Display",
    "fontSize": "52px",
    "fontStyle": "normal",
    "fontWeight": "500",
    "lineHeight": "60px",
    "marginBlockEnd": "16px",
    "marginBlockStart": 0,
    "@media(max-width: 768px)": {
        textAlign: "center"
    }
});

const subTitleStyles = css({
    "color": "#172B4D",
    "fontFamily": "Charlie Text",
    "fontSize": "20px",
    "fontStyle": "normal",
    "fontWeight": "400",
    "lineHeight": "28px",
    "marginBlockEnd": "32px",
    "marginBlockStart": 0,
    "@media(max-width: 768px)": {
        textAlign: "center"
    }
});

const TemplateHeaderSectionRelayFragment = graphql`
    fragment templateHeaderSectionRelayFragment on ContentPlatformTemplate {
        name
        oneLinerHeadline
        pluginModuleKey
        ...templateAuthorRelayFragment
    }
`;

export type TemplateHeaderSectionProps = {
    template: templateHeaderSectionRelayFragment$key;
};

export const TemplateHeaderSection = ({
    template: templateRef
}: TemplateHeaderSectionProps) => {
    const template = useFragment(
        TemplateHeaderSectionRelayFragment,
        templateRef
    );
    const { name, oneLinerHeadline, pluginModuleKey } = template;

    const intl = useIntl();

    const templateName = intl.formatMessage(
        ConfluenceTemplateMessages.templateTitle,
        {
            templateName: name ?? ""
        }
    );

    return (
        <section css={headerSectionStyles} data-testid="TemplateHeaderSection">
            <h1 css={titleStyles}>{templateName}</h1>
            <TemplateAuthor template={template} />
            <p css={subTitleStyles}>{oneLinerHeadline}</p>
            <TemplateCtaButtons
                name={templateName}
                pluginModuleKey={pluginModuleKey || ""}
            />
        </section>
    );
};
